// packages/front/src/portfolio/portfolio-page.tsx

import {
  CORPORATE_ACTION_TYPE_OBJ,
  INCOME_EVENT_TYPE,
  INCOME_EVENT_TYPE_LABELS,
  PORTFOLIO_BLOCKED_DATA_COMPLETENESS_OBJ,
  PORTFOLIO_POSITION_AVAILABILITY_FILTER_OBJ,
  POSITION_HISTORY_CONVERSION_STATUS_OBJ,
  POSITION_HISTORY_EVENT_TYPE_OBJ,
  POSITION_HISTORY_EVENT_TYPE_LABELS,
  PORTFOLIO_ACCOUNT_STATUS_FILTER,
  PORTFOLIO_ACCOUNT_STATUS_FILTER_LABELS,
  PORTFOLIO_ACCOUNT_STATUS_FILTER_OBJ,
  PORTFOLIO_ACCOUNT_STATUS_FILTER_UI_DEFAULT,
  PORTFOLIO_ACCOUNT_STATUS_OBJ,
  TRANSACTION_TYPE_OBJ,
  type TPortfolioAccountStatusFilter,
  type TPortfolioPositionAvailabilityFilter,
  isPortfolioPriceSyncActive,
  PORTFOLIO_PRICE_SYNC_STATUS_OBJ,
  PRICE_SOURCE_OBJ,
} from "@signals/shared";
import type {
  PortfolioBlockedSummaryMetric,
  PortfolioBlockedSummaryMetrics,
} from "@signals/shared";
import type { ColumnDef } from "@tanstack/react-table";
import {
  ChevronDown,
  DollarSign,
  Landmark,
  Link2,
  LockKeyhole,
  Pencil,
  Trash2,
  TrendingDown,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataPage,
  ErrorState,
  FILTER_TYPE_OBJ,
  formatCurrency,
  Loading,
  Select,
  SmartTable,
  ToastContainer,
  parseUrlState,
  serializeUrlState,
  useToast,
  type FilterClause,
  type SelectList,
  type SmartTableColumnMeta,
  type SortClause,
  type TableUrlState,
} from "../_common";
import { useRefreshSymbolPrice } from "../symbol";
import {
  ComparePeriodControls,
  ComparePeriodResults,
} from "./compare-panel";
import { CashBalancesMetric } from "./cash-balances-metric";
import { BrokerSyncPanel } from "./broker-sync-panel";
import { PortfolioFreshness } from "./portfolio-freshness";
import { CurrencySwitcher } from "./currency-switcher";
import { DateSelector } from "./date-selector";
import { PORTFOLIO_POSITION_VIEW_OBJ, type PortfolioPositionView } from "./portfolio-view.constants";
import { AddAccountModal } from "./modals/add-account-modal";
import { EditBrokerSyncAccountModal } from "./modals/edit-broker-sync-account-modal";
import { AddCashFlowModal } from "./modals/add-cashflow-modal";
import { AddExchangeRateModal } from "./modals/add-exchange-rate-modal";
import { AddIncomeEventModal } from "./modals/add-income-event-modal";
import { AddSymbolModal } from "./modals/add-symbol-modal";
import { AddTransactionModal } from "./modals/add-transaction-modal";
import { CloseAccountModal } from "./modals/close-account-modal";
import { TransactionBlockModal } from "./modals/transaction-block-modal";
import { FetchExchangeRateModal } from "./modals/fetch-exchange-rate-modal";
import { ImportTransactionsModal } from "./modals/import-transactions-modal";
import { ManualPriceModal, type ManualPriceTarget } from "./modals/manual-price-modal";
import { ProviderIdentityModal, type ProviderIdentityTarget } from "./modals/provider-identity-modal";
import { CorporateEventModal } from "./modals/corporate-event-modal";
import { SnapshotRevisionModal } from "./modals/snapshot-revision-modal";
import { CreatePortfolioModal, PortfolioSwitcher } from "./portfolio-switcher";
import {
  useDeleteIncomeEvent,
  useDeleteTransaction,
  useIncomeEvents,
  usePortfolioAccounts,
  usePortfolioCashflows,
  usePortfolioCompare,
  usePortfolioExchangeRates,
  usePortfolioPositionHistory,
  usePortfolioSummary,
  usePortfolioTransactions,
  usePortfolios,
  useRefreshExchangeRates,
  useUpdateTransaction,
  useYearlyPerformanceAll,
  type Account,
  type CashFlow,
  type IncomeEvent,
  type MissingValuation,
  type PortfolioSummary,
  type Position,
  type SymbolRecord,
  type TabRow,
  type Transaction,
  type YearlyPerformance,
} from "./portfolio.hooks";
import { useDisplayCurrency } from "./use-display-currency";
import { accountLabel } from "./portfolio-account-availability";

import { PORTFOLIO_ACCOUNT_FILTER_OBJ } from "../_deps";
import {
  Metric,
  isValuationIncomplete,
  isTransaction,
  missingValuationReasonLabel,
  PORTFOLIO_FILTER_COLUMN_OBJ,
  PORTFOLIO_MODAL_OBJ,
  PORTFOLIO_TAB_OBJ,
  READONLY_TAB_COLUMNS,
  TABS,
  aggregatePositionsBySymbol,
  applyClientFilters,
  buildAccountColumns,
  closedPositionColumns,
  extractServerFilters,
  isPortfolioServerFilter,
  formatNumber,
  formatPercent,
  formatUSD,
  numericValue,
  parseTransactionEdit,
  positionBaseColumns,
  positionHasIncompleteValuation,
  sliceData,
  toTabRows,
  today,
  type TPortfolioModal,
} from "./portfolio-page-helpers";

/**
 * Empty state of an allocation card. A loaded complete map without groups means the portfolio
 * holds nothing to allocate; an incomplete valuation keeps its own message, so the two are never mixed.
 */
function allocationEmptyMessage(isPartial: boolean, loaded: boolean): string {
  if (isPartial) return "No positive evaluated value to calculate percentages.";
  return loaded ? "Нет остатков для распределения" : "Нет данных";
}

function formatTableDate(value: unknown): string {
  if (!value) return "—";
  const dateValue = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(dateValue.getTime())) return String(value);
  const day = String(dateValue.getDate()).padStart(2, "0");
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${dateValue.getFullYear()}`;
}

type PositionAvailabilityFilter = TPortfolioPositionAvailabilityFilter;
type PortfolioTab = (typeof TABS)[number];

interface SummaryMetricWithBlockedProps {
  label: string;
  value: string;
  blockedMetric: PortfolioBlockedSummaryMetric | undefined;
  blockingState: PortfolioBlockedSummaryMetrics["blockingState"] | undefined;
  displayCurrency: string;
  valuationIncomplete: boolean;
  valuationDependent: boolean;
  percentUnavailableMessage: string;
}

const SummaryMetricWithBlocked: FC<SummaryMetricWithBlockedProps> = ({
  label,
  value,
  blockedMetric,
  blockingState,
  displayCurrency,
  valuationIncomplete,
  valuationDependent,
  percentUnavailableMessage,
}) => {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const hasAmount = blockedMetric?.amountUSD !== null && blockedMetric?.amountUSD !== undefined;
  const unavailablePositionCount = blockedMetric?.valuationCoverage?.unavailablePositionCount ?? 0;
  const amountLabel = blockedMetric?.completeness === PORTFOLIO_BLOCKED_DATA_COMPLETENESS_OBJ.PARTIAL ? "Known amount" : "Blocked amount";

  return (
    <div>
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <span>{label}</span>
        <div
          className="relative inline-flex"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={(event) => {
            if (!event.currentTarget.contains(document.activeElement)) setOpen(false);
          }}
        >
          <button
            type="button"
            className="rounded-sm p-0.5 text-amber-700 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
            aria-label={`Show blocked amount for ${label}`}
            aria-describedby={open ? tooltipId : undefined}
            aria-controls={open ? tooltipId : undefined}
            aria-expanded={open}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setOpen(false);
              }
            }}
            onBlur={(event) => {
              const wrapper = event.currentTarget.parentElement;
              if (!wrapper || !(event.relatedTarget instanceof Node) || !wrapper.contains(event.relatedTarget)) {
                setOpen(false);
              }
            }}
          >
            <LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          {open && (
            <div
              id={tooltipId}
              role="tooltip"
              className="absolute left-0 top-full z-50 mt-1 w-64 max-w-[80vw] rounded-md border bg-white p-3 text-xs text-slate-900 shadow-lg dark:bg-slate-950 dark:text-slate-100"
            >
              {hasAmount ? (
                <p className="font-medium">
                  {amountLabel}: {formatCurrency(blockedMetric?.displayAmount, displayCurrency)}
                </p>
              ) : (
                <p className="font-medium">No data</p>
              )}
              {blockedMetric?.percent !== null && blockedMetric?.percent !== undefined && (
                <p>{formatPercent(blockedMetric.percent)} of {label}</p>
              )}
              {blockingState?.completeness === PORTFOLIO_BLOCKED_DATA_COMPLETENESS_OBJ.PARTIAL && (
                <p className="mt-1 text-amber-800">
                  Lock data is incomplete for {blockingState.unknownPositionCount} position(s).
                </p>
              )}
              {blockingState?.completeness === PORTFOLIO_BLOCKED_DATA_COMPLETENESS_OBJ.UNKNOWN && (
                <p className="mt-1 text-amber-800">No lock data is available.</p>
              )}
              {unavailablePositionCount > 0 && (
                <p className="mt-1 text-amber-800">
                  Market valuation is unavailable for {unavailablePositionCount} blocked position(s).
                </p>
              )}
              {valuationDependent && valuationIncomplete && blockedMetric?.percent == null && (
                <p className="mt-1 text-amber-800">Portfolio valuation is incomplete, so the share cannot be calculated.</p>
              )}
              {blockedMetric?.percent == null && hasAmount && (
                <p className="mt-1 text-muted-foreground">{percentUnavailableMessage}</p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
};

function mapPortfolioTableStates(
  states: Record<PortfolioTab, TableUrlState>,
  update: (state: TableUrlState) => TableUrlState,
): Record<PortfolioTab, TableUrlState> {
  const nextStates = { ...states };
  for (const tab of TABS) nextStates[tab] = update(states[tab]);
  return nextStates;
}

export const PortfolioPage: FC = () => {
  const [date, setDate] = useState<string>(today());
  const [snapshotHistoryOpen, setSnapshotHistoryOpen] = useState(false);
  const [compareFrom, setCompareFrom] = useState("");
  const [compareTo, setCompareTo] = useState("");
  const [hasCompared, setHasCompared] = useState(false);
  const [activeTab, setActiveTab] = useState<PortfolioTab>(
    PORTFOLIO_TAB_OBJ.POSITIONS,
  );
  const [blockedTransactionsOnly, setBlockedTransactionsOnly] = useState(false);
  const [positionAvailabilityFilter, setPositionAvailabilityFilter] =
    useState<PositionAvailabilityFilter>(PORTFOLIO_POSITION_AVAILABILITY_FILTER_OBJ.ALL);
  const [blockingTransaction, setBlockingTransaction] = useState<Transaction | null>(null);
  const [pendingBlockConfirmation, setPendingBlockConfirmation] = useState<{
    id: string;
    blockedQuantity: number;
  } | null>(null);
  const [modal, setModal] = useState<TPortfolioModal | null>(null);
  const { toasts, dismissToast, showToast } = useToast();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const [createPortfolioOpen, setCreatePortfolioOpen] = useState(false);
  // Accounts tab only: filters the catalog by status on the selected date, never the financial data.
  const [accountStatusFilter, setAccountStatusFilter] = useState<TPortfolioAccountStatusFilter>(
    PORTFOLIO_ACCOUNT_STATUS_FILTER_UI_DEFAULT,
  );
  const [closingAccountId, setClosingAccountId] = useState<string | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);

  // Portfolio selector — portfolioId is stored in URL query param (?portfolioId=...)
  // so links are shareable. Falls back to first portfolio from API if not set.
  const portfoliosQuery = usePortfolios();
  const portfolios = portfoliosQuery.data?.items ?? [];
  const [portfolioId, setPortfolioIdState] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("portfolioId") ?? "";
  });
  // Active account selector (view-state, ) — drives server-side
  // accountId filter via FilterClause in urlState.filters.
  const [activeAccountId, setActiveAccountId] = useState<
    string | typeof PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL
  >(PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL);
  const [positionView, setPositionView] = useState<PortfolioPositionView>(PORTFOLIO_POSITION_VIEW_OBJ.ACCOUNTS);

  // Position-click filter — selects the conversion-aware history endpoint
  // while the Transactions tab is open. Cleared by the "Show all
  // transactions" button or by switching tabs manually.
  const [positionFilter, setPositionFilter] = useState<{
    symbolId: string;
    accountId?: string;
    label: string;
  } | null>(null);

  // Each Portfolio table keeps its own sorting, filters, pagination, and
  // column settings while the user moves between tabs. URL state on initial
  // load belongs to Positions; the URL then follows the active table.
  const [tableUrlStates, setTableUrlStates] = useState<
    Record<PortfolioTab, TableUrlState>
  >(() => {
    const initialSearchParams = new URLSearchParams(window.location.search);
    return {
      [PORTFOLIO_TAB_OBJ.POSITIONS]: parseUrlState(initialSearchParams),
      [PORTFOLIO_TAB_OBJ.CLOSED]: parseUrlState(new URLSearchParams()),
      [PORTFOLIO_TAB_OBJ.TRANSACTIONS]: parseUrlState(new URLSearchParams()),
      [PORTFOLIO_TAB_OBJ.INCOME_EVENTS]: parseUrlState(new URLSearchParams()),
      [PORTFOLIO_TAB_OBJ.CASH_FLOW]: parseUrlState(new URLSearchParams()),
      [PORTFOLIO_TAB_OBJ.EXCHANGE_RATES]: parseUrlState(new URLSearchParams()),
      [PORTFOLIO_TAB_OBJ.ACCOUNTS]: parseUrlState(new URLSearchParams()),
      [PORTFOLIO_TAB_OBJ.YEARLY_PERFORMANCE]: parseUrlState(new URLSearchParams()),
    };
  });
  // Position drilldown has a temporary table state so it does not overwrite
  // the user's saved Transactions tab state.
  const [drilledTransactionsUrlState, setDrilledTransactionsUrlState] =
    useState(() => parseUrlState(new URLSearchParams()));
  const isPositionDrilldown =
    activeTab === PORTFOLIO_TAB_OBJ.TRANSACTIONS && positionFilter !== null;
  const urlState: TableUrlState = isPositionDrilldown
    ? drilledTransactionsUrlState
    : tableUrlStates[activeTab];
  const setUrlState = useCallback<
    Dispatch<SetStateAction<TableUrlState>>
  >(
    (nextState) => {
      if (isPositionDrilldown) {
        setDrilledTransactionsUrlState(nextState);
        return;
      }
      setTableUrlStates((prev) => ({
        ...prev,
        [activeTab]:
          typeof nextState === "function"
            ? nextState(prev[activeTab] ?? parseUrlState(new URLSearchParams()))
            : nextState,
      }));
    },
    [activeTab, isPositionDrilldown],
  );

  const setPortfolioId = useCallback((id: string) => {
    // Reset portfolio-owned selection in the same render as the portfolio ID,
    // so no request combines the new portfolio with the previous account.
    setPositionFilter(null);
    setActiveAccountId(PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL);
    setBlockedTransactionsOnly(false);
    setPositionAvailabilityFilter(PORTFOLIO_POSITION_AVAILABILITY_FILTER_OBJ.ALL);
    setHasCompared(false);
    const resetAccountFilter = (prev: TableUrlState): TableUrlState => ({
      ...prev,
      page: 1,
      filters: prev.filters.filter((filter) => filter.column !== PORTFOLIO_FILTER_COLUMN_OBJ.ACCOUNT_ID),
    });
    setTableUrlStates((prev) =>
      mapPortfolioTableStates(prev, resetAccountFilter),
    );
    setDrilledTransactionsUrlState(resetAccountFilter);
    setPortfolioIdState(id);
    const params = new URLSearchParams(window.location.search);
    if (id) params.set("portfolioId", id);
    else params.delete("portfolioId");
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    window.history.replaceState(null, "", newUrl);
  }, []);
  useEffect(() => {
    if (!portfolioId && portfolios.length > 0 && portfolios[0]?.id) {
      setPortfolioId(portfolios[0].id);
    }
  }, [portfolioId, portfolios, setPortfolioId]);

  // Server-side filter params extracted from FilterClause[] (accountId, from, to).
  // These are sent as query params to the backend; other filters stay client-side.
  const serverFilters = useMemo(
    () => extractServerFilters(urlState.filters),
    [urlState.filters],
  );

  // Client-side filters — everything NOT handled by the backend (i.e. not
  // accountId select-single and not date date-range). Applied to the row
  // array before pagination in the *Sliced memos below. Without this, filter
  // chips (ticker, type, currency, …) display but never affect the rows.
  const clientFilters = useMemo(
    () =>
      urlState.filters.filter((filter) => !isPortfolioServerFilter(filter)),
    [urlState.filters],
  );

  // Sync activeAccountId → urlState.filters: add/remove accountId FilterClause.
  // "all" removes the clause; a specific account adds/replaces it.
  useEffect(() => {
    const syncAccountFilter = (prev: TableUrlState): TableUrlState => {
      const otherFilters = prev.filters.filter(
        (f) =>
          !(
            f.type === FILTER_TYPE_OBJ.SELECT_SINGLE &&
            f.column === PORTFOLIO_FILTER_COLUMN_OBJ.ACCOUNT_ID
          ),
      );
      if (activeAccountId === PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL) {
        return { ...prev, filters: otherFilters, page: 1 };
      }
      return {
        ...prev,
        filters: [
          ...otherFilters,
          {
            type: "select-single",
            column: "accountId",
            value: activeAccountId,
          } satisfies FilterClause,
        ],
        page: 1,
      };
    };
    setTableUrlStates((prev) =>
      mapPortfolioTableStates(prev, syncAccountFilter),
    );
    setDrilledTransactionsUrlState(syncAccountFilter);
  }, [activeAccountId]);

  // ── TanStack Query hooks ───────────────────────────────────────
  // All reads go through hooks — no inline apiFetch. Mutations auto-invalidate
  // in onSuccess, so no manual refetch is needed after create/update/delete.

  const summaryQuery = usePortfolioSummary(portfolioId, date);
  const compareQuery = usePortfolioCompare(
    portfolioId,
    compareFrom,
    compareTo,
    hasCompared && Boolean(compareFrom) && Boolean(compareTo),
  );
  // Every account with its status on the selected date: selectors and names include closed ones.
  const accountsQuery = usePortfolioAccounts(portfolioId, {
    date,
    status: PORTFOLIO_ACCOUNT_STATUS_FILTER_OBJ.ALL,
  });
  const accountsCatalogQuery = usePortfolioAccounts(portfolioId, {
    date,
    status: accountStatusFilter,
  });
  const transactionsQuery = usePortfolioTransactions(
    portfolioId,
    serverFilters,
  );
  const positionHistoryQuery = usePortfolioPositionHistory(
    portfolioId,
    positionFilter?.symbolId ?? "",
    date,
    {
      ...(positionFilter?.accountId ?? serverFilters.accountId
        ? { accountId: positionFilter?.accountId ?? serverFilters.accountId }
        : {}),
      ...(serverFilters.from ? { from: serverFilters.from } : {}),
      ...(serverFilters.to ? { to: serverFilters.to } : {}),
    },
    activeTab === PORTFOLIO_TAB_OBJ.TRANSACTIONS && positionFilter !== null,
  );
  const cashflowsQuery = usePortfolioCashflows(portfolioId, serverFilters);
  const exchangeRatesQuery = usePortfolioExchangeRates();
  const incomeEventsQuery = useIncomeEvents(portfolioId, serverFilters);
  const yearlyPerformanceAllQuery = useYearlyPerformanceAll(
    portfolioId,
    serverFilters.accountId,
    activeTab === "Yearly Performance",
  );

  // Mutations
  const updateTransaction = useUpdateTransaction(portfolioId);
  const deleteTransaction = useDeleteTransaction();
  const deleteIncomeEvent = useDeleteIncomeEvent();
  const refreshSymbolPrice = useRefreshSymbolPrice();
  const refreshRates = useRefreshExchangeRates();

  // ── Derived state from queries ─────────────────────────────────────────────

  const summary = summaryQuery.data ?? null;
  // Summary returns valuation totals and the positions used to calculate them
  // from the same read. Use those rows directly so the table cannot show prices
  // from a separate request revision.
  const allPositions = summary?.positions ?? [];
  const positions = useMemo(
    () => allPositions.filter((position) =>
      (position.quantity ?? 0) > 0 &&
      (!serverFilters.accountId || position.accountId === serverFilters.accountId),
    ),
    [allPositions, serverFilters.accountId],
  );
  const closedPositions = useMemo(
    () => allPositions.filter((position) =>
      (position.quantity ?? 0) <= 0 &&
      (!serverFilters.accountId || position.accountId === serverFilters.accountId),
    ),
    [allPositions, serverFilters.accountId],
  );
  const positionsForView = useMemo(
    () =>
      positionView === PORTFOLIO_POSITION_VIEW_OBJ.SECURITIES
        ? aggregatePositionsBySymbol(positions)
        : positions,
    [positions, positionView],
  );
  const accounts = accountsQuery.data?.items ?? [];
  const catalogAccounts = accountsCatalogQuery.data?.items ?? [];
  const accountNames = useMemo(
    () => new Map(accounts.map((account) => [account.id, account.name])),
    [accounts],
  );
  const closedAccountIds = useMemo(
    () => new Set(
      accounts
        .filter((account) => account.status === PORTFOLIO_ACCOUNT_STATUS_OBJ.CLOSED)
        .map((account) => account.id),
    ),
    [accounts],
  );

  const positionHistoryRows = useMemo<TabRow[]>(
    () =>
      (positionHistoryQuery.data?.items ?? []).map((event) =>
        event.eventType === CORPORATE_ACTION_TYPE_OBJ.CONVERSION
          ? {
              ...event,
              date: event.sourceDate,
              type: "conversion",
              quantity: event.targetQuantity,
              symbolTicker: `${event.sourceTicker} → ${event.targetTicker}`,
              symbolName: `${event.sourceName ?? event.sourceTicker} → ${event.targetName ?? event.targetTicker}`,
            }
          : { ...event },
      ),
    [positionHistoryQuery.data],
  );

  const loading = summaryQuery.isLoading;
  const error = summaryQuery.error?.message ?? null;
  const positionsLoading = summaryQuery.isLoading;
  const positionsError = summaryQuery.error?.message ?? null;

  // Active tab data — derived from the appropriate query.
  const tabData = useMemo<TabRow[]>(() => {
    switch (activeTab) {
      case PORTFOLIO_TAB_OBJ.TRANSACTIONS:
        return positionFilter
          ? positionHistoryRows
          : transactionsQuery.data?.items ?? [];
      case "Income Events":
        return toTabRows(incomeEventsQuery.data?.items ?? []);
      case "Cash Flow":
        return cashflowsQuery.data?.items ?? [];
      case "Exchange Rates":
        return exchangeRatesQuery.data?.items ?? [];
      case PORTFOLIO_TAB_OBJ.ACCOUNTS:
        return toTabRows(catalogAccounts);
      default:
        return [];
    }
  }, [
    activeTab,
    positionFilter,
    positionHistoryRows,
    transactionsQuery.data,
    incomeEventsQuery.data,
    cashflowsQuery.data,
    exchangeRatesQuery.data,
    catalogAccounts,
  ]);

  const tabLoading = useMemo(() => {
    switch (activeTab) {
      case PORTFOLIO_TAB_OBJ.TRANSACTIONS:
        return positionFilter
          ? positionHistoryQuery.isLoading
          : transactionsQuery.isLoading;
      case "Income Events":
        return incomeEventsQuery.isLoading;
      case "Cash Flow":
        return cashflowsQuery.isLoading;
      case "Exchange Rates":
        return exchangeRatesQuery.isLoading;
      case PORTFOLIO_TAB_OBJ.ACCOUNTS:
        return accountsCatalogQuery.isLoading;
      default:
        return false;
    }
  }, [
    activeTab,
    positionFilter,
    positionHistoryQuery.isLoading,
    transactionsQuery.isLoading,
    incomeEventsQuery.isLoading,
    cashflowsQuery.isLoading,
    exchangeRatesQuery.isLoading,
    accountsCatalogQuery.isLoading,
  ]);

  // SelectList for accountId column — enables dropdown filter in SmartTable's FilterBar.
  const accountList: SelectList = useMemo(
    () => ({
      propertyKey: "accountId",
      items: accounts.map((a: Account) => ({
        value: a.id,
        label: { "en-US": accountLabel(a, date) },
      })),
    }),
    [accounts, date],
  );

  // Edit modal state for transactions / symbols / income events
  const [editTransaction, setEditTransaction] = useState<Transaction | null>(
    null,
  );
  const [editIncomeEvent, setEditIncomeEvent] = useState<IncomeEvent | null>(
    null,
  );
  const [editSymbol, setEditSymbol] = useState<SymbolRecord | null>(null);
  const [manualPriceTarget, setManualPriceTarget] = useState<ManualPriceTarget | null>(null);
  const [providerIdentityTarget, setProviderIdentityTarget] = useState<ProviderIdentityTarget | null>(null);
  const [corporateEventTarget, setCorporateEventTarget] = useState<{ symbolId: string; ticker: string; currency: string; quantity: number } | null>(null);

  // Pre-fill override for create-mode AddTransactionModal (e.g. Sell from a
  // position row). When set, takes precedence over the activeAccount prefill.
  const [transactionPrefill, setTransactionPrefill] = useState<
    Partial<Transaction> | undefined
  >(undefined);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    for (const key of ["page", "size", "preset", "sort", "filters", "sticky", "hidden"]) {
      params.delete(key);
    }
    new URLSearchParams(serializeUrlState(urlState)).forEach((value, key) => {
      params.set(key, value);
    });
    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }, [urlState]);

  const allocations = useMemo(
    () =>
      summary?.allocations ?? {
        allocationByAssetClass: summary?.allocationByAssetClass,
        allocationByCurrency: summary?.allocationByCurrency,
        allocationByAccount: summary?.allocationByAccount,
        allocationAmountsByAssetClass: summary?.allocationAmountsByAssetClass,
        allocationAmountsByCurrency: summary?.allocationAmountsByCurrency,
        partialAllocationByAssetClass: summary?.partialAllocationByAssetClass,
        partialAllocationByCurrency: summary?.partialAllocationByCurrency,
        partialAllocationByAccount: summary?.partialAllocationByAccount,
        partialAllocationAmountsByAssetClass: summary?.partialAllocationAmountsByAssetClass,
        partialAllocationAmountsByCurrency: summary?.partialAllocationAmountsByCurrency,
      },
    [summary],
  );

  // Close "More" dropdown on outside click / Escape
  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        moreRef.current &&
        e.target instanceof Node &&
        !moreRef.current.contains(e.target)
      ) {
        setMoreOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [moreOpen]);

  // ── Inline edit + delete handlers (mutations auto-invalidate) ──────────────

  const handleTransactionInlineEdit = useCallback(
    async (rowId: string, field: string, value: unknown) => {
      try {
        const body = parseTransactionEdit(field, value);
        if (!body) throw new Error(`Unsupported transaction field: ${field}`);
        await updateTransaction.mutateAsync({
          id: rowId,
          body,
        });
        showToast("success", `Updated ${field}`);
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast("error", `Save failed: ${message}`);
      }
    },
    [updateTransaction, showToast],
  );

  const handleDeleteTransaction = useCallback(
    async (id: string) => {
      if (!confirm("Delete this transaction?")) return;
      try {
        await deleteTransaction.mutateAsync(id);
        showToast("success", "Transaction deleted");
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast("error", `Delete failed: ${message}`);
      }
    },
    [deleteTransaction, showToast],
  );

  const handleDeleteIncomeEvent = useCallback(
    async (id: string) => {
      if (!confirm("Delete this income event?")) return;
      try {
        await deleteIncomeEvent.mutateAsync(id);
        showToast("success", "Income event deleted");
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast("error", `Delete failed: ${message}`);
      }
    },
    [deleteIncomeEvent, showToast],
  );

  const handleRefreshPrice = useCallback(
    async (symbolId: string, ticker: string, returnToFmp = false) => {
      try {
        if (returnToFmp) {
          const preview = await refreshSymbolPrice.mutateAsync({
            id: symbolId, portfolioId, date, returnToFmpMode: "preview",
          });
          if (!preview.candidate || preview.expectedMarketPriceRevision === undefined || !preview.expectedIdentityFingerprint) {
            throw new Error("FMP did not return a candidate that can be reviewed.");
          }
          const affectedCount = preview.affectedPortfolioIds?.length ?? 0;
          const apply = confirm(
            `FMP found ${preview.candidate.providerCode} at ${preview.candidate.price} ${preview.candidate.currency} per share, dated ${preview.candidate.priceDate}. Applying it replaces the manual price for ${ticker} in ${affectedCount} portfolio${affectedCount === 1 ? "" : "s"}. Apply this quote?`,
          );
          if (!apply) return;
          await refreshSymbolPrice.mutateAsync({
            id: symbolId,
            portfolioId,
            date,
            returnToFmpMode: "apply",
            expectedCandidate: {
              price: preview.candidate.price,
              priceDate: preview.candidate.priceDate,
              providerCode: preview.candidate.providerCode,
            },
            expectedMarketPriceRevision: preview.expectedMarketPriceRevision,
            expectedIdentityFingerprint: preview.expectedIdentityFingerprint,
          });
          showToast("success", `FMP price restored for ${ticker}`);
          return;
        }
        const result = await refreshSymbolPrice.mutateAsync({ id: symbolId, portfolioId, date });
        showToast("success", `FMP price check ${result.status}`);
      } catch (err) {
        const message = err instanceof Error ? err.message : "error";
        showToast("error", returnToFmp
          ? `FMP could not replace the manual price; it remains active. ${message}`
          : `Refresh price failed: ${message}`);
      }
    },
    [refreshSymbolPrice, showToast, portfolioId, date],
  );

  const handleSellPosition = useCallback(
    (
      symbolId: string,
      ticker: string,
      maxQty: number,
      currentPrice: number,
    ) => {
      setTransactionPrefill({
        type: "sell",
        symbolId,
        symbolTicker: ticker,
        quantity: maxQty,
        price: currentPrice,
        ...(activeAccountId !== PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL
          ? { accountId: activeAccountId }
          : {}),
      });
      setModal(PORTFOLIO_MODAL_OBJ.TRANSACTION);
    },
    [activeAccountId],
  );

  // Click a position row → load its trades and conversion chain from the
  // server. Aggregated security rows inherit the active account filter.
  const handlePositionClick = useCallback((pos: Position) => {
    const symbolId = pos.symbolId;
    if (!symbolId) return;
    const ticker = pos.symbolTicker ?? "—";
    setPositionFilter({
      symbolId,
      ...(pos.accountId ? { accountId: pos.accountId } : {}),
      label: ticker,
    });
    setDrilledTransactionsUrlState({
      ...urlState,
      page: 1,
      filters: urlState.filters.filter(isPortfolioServerFilter),
    });
    setActiveTab(PORTFOLIO_TAB_OBJ.TRANSACTIONS);
  }, [urlState]);

  const handleTabChange = useCallback((nextTab: PortfolioTab) => {
    setActiveTab(nextTab);
    setPositionFilter(null);
  }, []);

  const handleActiveAccountChange = useCallback((accountId: string) => {
    setActiveAccountId(accountId);
    if (positionFilter?.accountId && positionFilter.accountId !== accountId) {
      setPositionFilter(null);
    }
  }, [positionFilter]);

  const handleRefreshRates = useCallback(async () => {
    setMoreOpen(false);
    try {
      const res = await refreshRates.mutateAsync({});
      showToast("success", `Refreshed ${res.refreshed} exchange rates`);
    } catch {
      showToast("error", "Refresh failed");
    }
  }, [refreshRates, showToast]);

  const { displayCurrency } = useDisplayCurrency(portfolioId);

  const positionColumns = useMemo<ColumnDef<Position>[]>(() => {
    return [
      ...positionBaseColumns(displayCurrency, positionView === PORTFOLIO_POSITION_VIEW_OBJ.SECURITIES),
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          filterable: "none",
          sortable: "none",
          isAction: true,
        } satisfies SmartTableColumnMeta,
        cell: ({ row }) => {
          const symbolId = row.original.symbolId;
          const ticker = row.original.symbolTicker ?? "—";
          const qty = row.original.quantity ?? 0;
          const currentPrice = row.original.currentPrice ?? 0;
          const isManualPrice = row.original.marketPriceSource === PRICE_SOURCE_OBJ.MANUAL;
          const canPrefillSell =
            typeof row.original.currentPrice === "number" &&
            Number.isFinite(row.original.currentPrice) &&
            !positionHasIncompleteValuation(row.original);
          if (!symbolId) return null;
          const showSellAction = positionView === PORTFOLIO_POSITION_VIEW_OBJ.ACCOUNTS;
          return (
            <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-green-500 text-green-600 hover:bg-green-50 p-0"
                title={isManualPrice ? "Return to FMP" : "Refresh price"}
                aria-label={isManualPrice ? `Return to FMP for ${ticker}` : "Refresh price"}
                onClick={() => handleRefreshPrice(symbolId, ticker, isManualPrice)}
              >
                <DollarSign className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 p-0"
                aria-label={`Set manual price for ${ticker}`}
                title={`Set manual price for ${ticker}`}
                onClick={() => setManualPriceTarget({
                  id: symbolId,
                  ticker,
                  price: row.original.currentPrice ?? null,
                  priceDate: row.original.marketPriceDate ?? null,
                  quantity: qty,
                  currency: row.original.currency ?? displayCurrency,
                })}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 p-0"
                aria-label={`Correct FMP ticker for ${ticker}`}
                title={`Correct FMP ticker for ${ticker}`}
                onClick={() => setProviderIdentityTarget({ id: symbolId, ticker })}
              >
                <Link2 className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 p-0"
                aria-label={`Record broker event for ${ticker}`}
                title={`Record broker event for ${ticker}`}
                onClick={() => setCorporateEventTarget({
                  symbolId,
                  ticker,
                  currency: row.original.currency ?? displayCurrency,
                  quantity: qty,
                })}
              >
                <Landmark className="h-4 w-4" />
              </Button>
              {showSellAction && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-6 w-6 border-orange-500 text-orange-600 hover:bg-orange-50 p-0"
                  aria-label="Sell"
                  title={canPrefillSell ? "Sell" : "Sell (price unavailable)"}
                  disabled={qty <= 0 || !canPrefillSell}
                  onClick={() =>
                    handleSellPosition(symbolId, ticker, qty, currentPrice)
                  }
                >
                  <TrendingDown className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ];
  }, [handleRefreshPrice, handleSellPosition, displayCurrency, positionView]);

  // ── Column definitions (built via useMemo so Actions cells capture handlers) ─

  const transactionColumns = useMemo<ColumnDef<TabRow>[]>(() => {
    const base: ColumnDef<TabRow>[] = [
      {
        accessorKey: "date",
        header: "Date",
        meta: {
          format: "date",
          filterable: "client",
          filterType: "date-range",
          customCell: true,
        } satisfies SmartTableColumnMeta,
        cell: ({ row, getValue }) => {
          if (row.original["eventType"] !== CORPORATE_ACTION_TYPE_OBJ.CONVERSION) {
            return formatTableDate(getValue());
          }
          const sourceDate = row.original["sourceDate"];
          const targetDate = row.original["targetDate"];
          return `${formatTableDate(sourceDate)} → ${formatTableDate(targetDate)}`;
        },
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row, getValue }) => {
          if (row.original["eventType"] === POSITION_HISTORY_EVENT_TYPE_OBJ.SECURITY_TRANSFER) return POSITION_HISTORY_EVENT_TYPE_LABELS.security_transfer["en-US"];
          if (row.original["eventType"] === POSITION_HISTORY_EVENT_TYPE_OBJ.QUANTITY_CORRECTION) return POSITION_HISTORY_EVENT_TYPE_LABELS.quantity_correction["en-US"];
          if (row.original["eventType"] !== CORPORATE_ACTION_TYPE_OBJ.CONVERSION) {
            return String(getValue() ?? "—");
          }
          const status = row.original["status"];
          return `Conversion (${status === POSITION_HISTORY_CONVERSION_STATUS_OBJ.COMPLETED ? POSITION_HISTORY_CONVERSION_STATUS_OBJ.COMPLETED : POSITION_HISTORY_CONVERSION_STATUS_OBJ.PENDING})`;
        },
      },
      { accessorKey: "symbolTicker", header: "Ticker" },
      { accessorKey: "symbolName", header: "Name" },
      {
        accessorKey: "accountId",
        header: "Account",
        cell: ({ row }) => {
          const accountName = row.original["accountName"];
          return typeof accountName === "string" ? accountName : "—";
        },
        meta: {
          filterable: "client",
          filterType: "select-single",
          multiSelect: false,
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "quantity",
        header: "Qty",
        meta: {
          align: "right",
          editable: true,
          format: "number",
          customCell: true,
        } satisfies SmartTableColumnMeta,
        cell: ({ row, getValue }) =>
          row.original["eventType"] === POSITION_HISTORY_EVENT_TYPE_OBJ.QUANTITY_CORRECTION
            ? `${formatNumber(numericValue(row.original["quantityBefore"]))} → ${formatNumber(numericValue(row.original["quantityAfter"]))}`
            : row.original["eventType"] === CORPORATE_ACTION_TYPE_OBJ.CONVERSION
            ? `${formatNumber(numericValue(row.original["sourceQuantity"]))} → ${formatNumber(numericValue(row.original["targetQuantity"]))}`
            : formatNumber(numericValue(getValue())),
      },
      {
        accessorKey: "price",
        header: "Price",
        meta: {
          align: "right",
          editable: true,
          format: "number",
        } satisfies SmartTableColumnMeta,
        cell: ({ getValue }) => formatNumber(numericValue(getValue())),
      },
      {
        accessorKey: "commission",
        header: "Commission",
        meta: {
          align: "right",
          editable: true,
          format: "number",
        } satisfies SmartTableColumnMeta,
        cell: ({ getValue }) => formatNumber(numericValue(getValue())),
      },
      { accessorKey: "currency", header: "Currency" },
      {
        accessorKey: "note",
        header: "Note",
        meta: { editable: true } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "blockedQuantity",
        header: "Blocked Qty",
        meta: { align: "right" } satisfies SmartTableColumnMeta,
        cell: ({ getValue }) => {
          const blockedQuantity = numericValue(getValue()) ?? 0;
          return blockedQuantity > 0 ? formatNumber(blockedQuantity) : "—";
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          filterable: "none",
          sortable: "none",
          isAction: true,
        } satisfies SmartTableColumnMeta,
        cell: ({ row }) => {
          const id = row.original["id"];
          if (typeof id !== "string" || row.original["eventType"] === CORPORATE_ACTION_TYPE_OBJ.CONVERSION) return null;
          const transaction = isTransaction(row.original) ? row.original : null;
          if (!transaction) return null;
          const canMarkBlocked = transaction?.["type"] === TRANSACTION_TYPE_OBJ.BUY;
          return (
            <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
              {canMarkBlocked && transaction && (
                <Button
                  variant="outline"
                  size="icon"
                  className={`h-6 w-6 p-0 ${(numericValue(transaction["blockedQuantity"]) ?? 0) > 0 ? "border-amber-500 text-amber-700 hover:bg-amber-50" : ""}`}
                  aria-label="Mark purchase as blocked"
                  title={(numericValue(transaction["blockedQuantity"]) ?? 0) > 0 ? "Edit blocked quantity" : "Mark as blocked"}
                  onClick={() => setBlockingTransaction(transaction)}
                >
                  <LockKeyhole className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-blue-400 text-blue-600 hover:bg-blue-50 p-0"
                aria-label="Edit"
                title="Edit"
                onClick={() => {
                  if (transaction) setEditTransaction(transaction);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-red-500 text-red-600 hover:bg-red-50 p-0"
                aria-label="Delete"
                title="Delete"
                onClick={() => handleDeleteTransaction(id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ];
    return base;
  }, [handleDeleteTransaction]);

  const accountColumns = useMemo(
    () => buildAccountColumns(setClosingAccountId, setEditingAccountId),
    [],
  );
  const closingAccount = useMemo(
    () => accounts.find((account) => account.id === closingAccountId) ?? null,
    [accounts, closingAccountId],
  );
  const editingAccount = useMemo(
    () => accounts.find((account) => account.id === editingAccountId) ?? null,
    [accounts, editingAccountId],
  );

  const incomeEventColumns = useMemo<ColumnDef<TabRow>[]>(() => {
    const base: ColumnDef<TabRow>[] = [
      {
        accessorKey: "date",
        header: "Date",
        meta: {
          format: "date",
          filterable: "client",
          filterType: "date-range",
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ getValue }) => {
          const value = getValue();
          if (typeof value !== "string") return "—";
          const eventType = INCOME_EVENT_TYPE.find((type) => type === value);
          return eventType
            ? INCOME_EVENT_TYPE_LABELS[eventType]["en-US"]
            : value;
        },
      },
      { accessorKey: "symbolTicker", header: "Ticker" },
      { accessorKey: "symbolName", header: "Name" },
      {
        accessorKey: "accountId",
        header: "Account",
        cell: ({ row }) => {
          const accountName = row.original["accountName"];
          return typeof accountName === "string" ? accountName : "—";
        },
        meta: {
          filterable: "client",
          filterType: "select-single",
          multiSelect: false,
        } satisfies SmartTableColumnMeta,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        meta: {
          align: "right",
          format: "number",
        } satisfies SmartTableColumnMeta,
        cell: ({ getValue }) => formatNumber(numericValue(getValue())),
      },
      { accessorKey: "currency", header: "Currency" },
      { accessorKey: "note", header: "Note" },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          filterable: "none",
          sortable: "none",
          isAction: true,
        } satisfies SmartTableColumnMeta,
        cell: ({ row }) => {
          const id = row.original["id"];
          if (typeof id !== "string") return null;
          const incomeEvent = incomeEventsQuery.data?.items.find(
            (item) => item.id === id,
          );
          return (
            <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-blue-400 text-blue-600 hover:bg-blue-50 p-0"
                aria-label="Edit"
                title="Edit"
                onClick={() => {
                  if (incomeEvent) setEditIncomeEvent(incomeEvent);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 border-red-500 text-red-600 hover:bg-red-50 p-0"
                aria-label="Delete"
                title="Delete"
                onClick={() => handleDeleteIncomeEvent(id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ];
    return base;
  }, [handleDeleteIncomeEvent]);

  // ── Sliced data for SmartTable (client-side pagination + sorting) ────────────

  // Apply client-side filters (ticker, type, currency, …) BEFORE pagination
  // so that filtering affects the whole dataset, not just the current page.
  const positionsFiltered = useMemo(() => {
    const availabilityFiltered = positionsForView.filter((position) => {
      if (positionAvailabilityFilter === PORTFOLIO_POSITION_AVAILABILITY_FILTER_OBJ.BLOCKED) {
        return position.hasBlockedQuantity === true;
      }
      if (positionAvailabilityFilter === PORTFOLIO_POSITION_AVAILABILITY_FILTER_OBJ.AVAILABLE) {
        return typeof position.availableQuantity === "number" &&
          Number.isFinite(position.availableQuantity) &&
          position.availableQuantity > 0;
      }
      return true;
    });
    return applyClientFilters(availabilityFiltered, clientFilters);
  }, [positionsForView, clientFilters, positionAvailabilityFilter]);
  const positionsSliced = useMemo(
    () =>
      sliceData(
        positionsFiltered,
        urlState.sort,
        urlState.page,
        urlState.pageSize,
      ),
    [positionsFiltered, urlState.sort, urlState.page, urlState.pageSize],
  );

  const transactionsFiltered = useMemo(() => {
    const clientFiltered = applyClientFilters(tabData, clientFilters);
    const blockedFiltered = activeTab === PORTFOLIO_TAB_OBJ.TRANSACTIONS && blockedTransactionsOnly
      ? clientFiltered.filter((row) => (numericValue(row["blockedQuantity"]) ?? 0) > 0)
      : clientFiltered;
    return blockedFiltered;
  }, [tabData, clientFilters, activeTab, blockedTransactionsOnly, positionFilter]);

  const transactionsSliced = useMemo(() => {
    return sliceData(transactionsFiltered, urlState.sort, urlState.page, urlState.pageSize);
  }, [transactionsFiltered, urlState.sort, urlState.page, urlState.pageSize]);

  // Show success only after the active page of the transaction table has
  // rendered the server-confirmed block quantity. Removing a row from
  // "blocked only" also confirms a successful unblock.
  useEffect(() => {
    const pending = pendingBlockConfirmation;
    if (!pending || activeTab !== PORTFOLIO_TAB_OBJ.TRANSACTIONS) return;

    const row = transactionsSliced.find((item) => item["id"] === pending.id);
    const rowShowsSavedQuantity = row !== undefined &&
      (numericValue(row["blockedQuantity"]) ?? 0) === pending.blockedQuantity;
    const rowWasRemovedFromBlockedOnlyView =
      pending.blockedQuantity === 0 &&
      blockedTransactionsOnly &&
      !transactionsFiltered.some((item) => item["id"] === pending.id);

    if (!rowShowsSavedQuantity && !rowWasRemovedFromBlockedOnlyView) return;
    showToast("success", "Blocked quantity updated");
    setPendingBlockConfirmation(null);
  }, [
    activeTab,
    blockedTransactionsOnly,
    pendingBlockConfirmation,
    showToast,
    transactionsFiltered,
    transactionsSliced,
  ]);

  // Read-only tabs (Cash Flow, Exchange Rates, Accounts) share the same
  // client-side slice as Transactions — tabData is swapped on tab change.
  const readonlySliced = useMemo(
    () =>
      sliceData(
        applyClientFilters(tabData, clientFilters),
        urlState.sort,
        urlState.page,
        urlState.pageSize,
      ),
    [tabData, clientFilters, urlState.sort, urlState.page, urlState.pageSize],
  );

  // Closed positions tab — client-side filter + paginate, same as Positions.
  const closedSliced = useMemo(
    () =>
      sliceData(
        applyClientFilters(
          closedPositions,
          clientFilters,
        ),
        urlState.sort,
        urlState.page,
        urlState.pageSize,
      ),
    [
      closedPositions,
      clientFilters,
      urlState.sort,
      urlState.page,
      urlState.pageSize,
    ],
  );

  // Filtered totals (after client-side filters, before pagination) — used as
  // SmartTable `total` so the pager reflects the filtered row count, not the
  // raw query length.
  const tabDataFiltered = useMemo(
    () => applyClientFilters(tabData, clientFilters),
    [tabData, clientFilters],
  );
  const closedFilteredTotal = useMemo(
    () =>
      applyClientFilters(closedPositions, clientFilters)
        .length,
    [closedPositions, clientFilters],
  );

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderAllocations = (
    label: string,
    data: Record<string, number> | undefined,
    amounts: PortfolioSummary["allocationAmountsByAssetClass"],
    isPartial = false,
  ) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {isPartial ? `${label} — evaluated portion` : label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {new Set([...Object.keys(data ?? {}), ...Object.keys(amounts ?? {})]).size > 0 ? (
          <ul className="space-y-1 text-sm">
            {[...new Set([...Object.keys(data ?? {}), ...Object.keys(amounts ?? {})])].map((k) => (
              <li key={k} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3">
                <span className="min-w-0 break-words">{k}</span>
                <span className="min-w-0 text-right">
                  <span className="block whitespace-nowrap">
                    {formatCurrency(amounts?.[k]?.displayValue, displayCurrency)}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {formatPercent(data?.[k])}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            {allocationEmptyMessage(isPartial, data !== undefined || amounts !== undefined)}
          </p>
        )}
      </CardContent>
    </Card>
  );

  const renderAccountAllocations = (
    data: Record<string, { name: string; valueUSD: number; displayValue: number | null; percent: number }> | undefined,
    isPartial: boolean,
  ) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {isPartial ? "By Account — evaluated portion" : "By Account"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data && Object.keys(data).length > 0 ? (
          <ul className="space-y-1 text-sm">
            {Object.entries(data).map(([accountId, allocation]) => (
              <li key={accountId} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3">
                <span className="min-w-0 break-words">{accounts.find((account) => account.id === accountId)?.name ?? allocation.name}</span>
                <span className="min-w-0 text-right">
                  <span className="block whitespace-nowrap">
                    {formatCurrency(allocation.displayValue, displayCurrency)}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {formatPercent(allocation.percent)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            {allocationEmptyMessage(isPartial, data !== undefined)}
          </p>
        )}
      </CardContent>
    </Card>
  );

  const renderValuationNotice = (
    status: "complete" | "incomplete" | undefined,
    missingValuations: MissingValuation[] | undefined,
    partialValueCoverage: PortfolioSummary["partialValueCoverage"],
    partialUnrealizedPnLCoverage: PortfolioSummary["partialUnrealizedPnLCoverage"],
    inferredIncomplete = false,
  ) => {
    if (
      !inferredIncomplete &&
      !isValuationIncomplete({ valuationStatus: status, missingValuations })
    ) {
      return null;
    }
    const missing = missingValuations ?? [];
    const unavailablePositionCount = new Set(missing.map((item) =>
      `${item.accountId ?? ""}|${item.symbolId ?? item.symbol ?? item.name ?? "unknown"}`,
    )).size;
    const formatCount = (count: number, singular: string, plural: string) =>
      `${count} ${count === 1 ? singular : plural}`;
    return (
      <div
        role="status"
        className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
      >
        <p>
          Full portfolio valuation is incomplete. The estimated metrics below include only amounts that could be calculated.
        </p>
        {partialValueCoverage && partialUnrealizedPnLCoverage ? (
          <p className="mt-1">
            Estimated value covers {formatCount(partialValueCoverage.positions.length, "position", "positions")} and {formatCount(partialValueCoverage.cashBalances?.length ?? 0, "cash balance", "cash balances")}; unrealized P&amp;L covers {formatCount(partialUnrealizedPnLCoverage.positions.length, "position", "positions")}. {formatCount(unavailablePositionCount, "position", "positions")} unavailable.
          </p>
        ) : (
          <p className="mt-1">Coverage details are unavailable for this snapshot.</p>
        )}
        {missing.length > 0 && (
          <ul className="mt-2 list-inside list-disc space-y-1">
            {missing.map((item) => (
              <li key={`${item.symbolId}:${item.reason}`}>
                <span className="font-medium">
                  {item.symbol || item.name || item.symbolId || "Unknown instrument"}
                </span>
                {`: ${missingValuationReasonLabel(item.reason)}`}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const smartTableCommonProps = {
    total: 0,
    page: urlState.page,
    pageSize: urlState.pageSize,
    isLoading: false,
    error: null,
    filters: urlState.filters satisfies FilterClause[],
    sort: urlState.sort,
    lists: [accountList] satisfies SelectList[],
    onPageChange: (page: number) => setUrlState((prev) => ({ ...prev, page })),
    onPageSizeChange: (pageSize: number) =>
      setUrlState((prev) => ({ ...prev, pageSize, page: 1 })),
    onFiltersChange: (filters: FilterClause[]) =>
      setUrlState((prev) => ({ ...prev, filters, page: 1 })),
    onSortChange: (sort: SortClause[]) =>
      setUrlState((prev) => ({ ...prev, sort })),
  };

  const renderTabContent = () => {
    if (activeTab === PORTFOLIO_TAB_OBJ.POSITIONS) {
      if (positionsLoading) return <Loading />;
      return (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1" role="group" aria-label="Position view">
              <Button
                variant={positionView === PORTFOLIO_POSITION_VIEW_OBJ.ACCOUNTS ? "default" : "outline"}
                size="sm"
                aria-pressed={positionView === PORTFOLIO_POSITION_VIEW_OBJ.ACCOUNTS}
                onClick={() => {
                  setPositionView(PORTFOLIO_POSITION_VIEW_OBJ.ACCOUNTS);
                  setUrlState((prev) => ({ ...prev, page: 1 }));
                }}
              >
                By account
              </Button>
              <Button
                variant={positionView === PORTFOLIO_POSITION_VIEW_OBJ.SECURITIES ? "default" : "outline"}
                size="sm"
                aria-pressed={positionView === PORTFOLIO_POSITION_VIEW_OBJ.SECURITIES}
                onClick={() => {
                  setPositionView(PORTFOLIO_POSITION_VIEW_OBJ.SECURITIES);
                  setUrlState((prev) => ({ ...prev, page: 1 }));
                }}
              >
                By security
              </Button>
            </div>
            <div className="flex gap-1" role="group" aria-label="Position availability">
              {([
                { value: "all", label: "All" },
                { value: "blocked", label: "Blocked" },
                { value: "available", label: "Available" },
              ] as const).map((filter) => (
                <Button
                  key={filter.value}
                  variant={positionAvailabilityFilter === filter.value ? "default" : "outline"}
                  size="sm"
                  aria-pressed={positionAvailabilityFilter === filter.value}
                  onClick={() => {
                    setPositionAvailabilityFilter(filter.value);
                    setUrlState((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
          <SmartTable<Position>
            {...smartTableCommonProps}
            columns={positionColumns}
            data={positionsSliced}
            total={positionsFiltered.length}
            isLoading={positionsLoading}
            error={positionsError}
            onRowClick={handlePositionClick}
          />
        </div>
      );
    }
    if (activeTab === PORTFOLIO_TAB_OBJ.CLOSED) {
      const closedLoading = positionsLoading;
      if (closedLoading) return <Loading />;
      return (
        <SmartTable<Position>
          {...smartTableCommonProps}
          columns={closedPositionColumns(displayCurrency)}
          data={closedSliced}
          total={closedFilteredTotal}
          isLoading={closedLoading}
          error={positionsError}
          onRowClick={(row) => {
            handlePositionClick(row);
          }}
        />
      );
    }
    if (tabLoading) return <Loading />;

    if (activeTab === PORTFOLIO_TAB_OBJ.TRANSACTIONS) {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={blockedTransactionsOnly ? "default" : "outline"}
              aria-pressed={blockedTransactionsOnly}
              onClick={() => {
                setBlockedTransactionsOnly((current) => !current);
                setUrlState((prev) => ({ ...prev, page: 1 }));
              }}
            >
              {blockedTransactionsOnly ? "Show all deals" : `Show blocked only (${tabDataFiltered.filter((row) => (numericValue(row["blockedQuantity"]) ?? 0) > 0).length})`}
            </Button>
          </div>
          {positionFilter && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5 text-sm">
              <span className="text-muted-foreground">
                Filtered by position:
              </span>
              <span className="font-medium">
                {positionFilter.label}
                {(positionFilter.accountId ?? serverFilters.accountId)
                  ? ` on ${accounts.find((account) => account.id === (positionFilter.accountId ?? serverFilters.accountId))?.name ?? "—"}`
                  : ""}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs"
                onClick={() => setPositionFilter(null)}
              >
                Show all transactions
              </Button>
            </div>
          )}
          <SmartTable
            {...smartTableCommonProps}
            columns={transactionColumns}
            data={transactionsSliced}
            total={transactionsFiltered.length}
            error={positionFilter
              ? positionHistoryQuery.error?.message ?? null
              : transactionsQuery.error?.message ?? null}
            onInlineEdit={handleTransactionInlineEdit}
            isRowEditable={isTransaction}
            rowIdField="id"
          />
        </div>
      );
    }

    if (activeTab === "Income Events") {
      return (
        <SmartTable
          {...smartTableCommonProps}
          columns={incomeEventColumns}
          data={transactionsSliced}
          total={tabDataFiltered.length}
          rowIdField="id"
        />
      );
    }

    if (activeTab === "Yearly Performance") {
      return renderYearlyPerformance();
    }

    if (activeTab === PORTFOLIO_TAB_OBJ.ACCOUNTS) {
      return (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Состояние на {date}:</span>
            <Select
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
              value={accountStatusFilter}
              onChange={(value) =>
                setAccountStatusFilter(
                  PORTFOLIO_ACCOUNT_STATUS_FILTER.find((item) => item === value) ??
                    PORTFOLIO_ACCOUNT_STATUS_FILTER_UI_DEFAULT,
                )
              }
              options={PORTFOLIO_ACCOUNT_STATUS_FILTER.map((item) => ({
                value: item,
                label: PORTFOLIO_ACCOUNT_STATUS_FILTER_LABELS[item]["ru-RU"] ?? item,
              }))}
              ariaLabel="Account status filter"
            />
          </div>
          <SmartTable
            {...smartTableCommonProps}
            columns={accountColumns}
            data={readonlySliced}
            total={tabDataFiltered.length}
            isLoading={tabLoading}
            rowIdField="id"
          />
          <BrokerSyncPanel portfolioId={portfolioId} />
        </div>
      );
    }

    // Read-only tabs (Cash Flow, Exchange Rates) — SmartTable
    // without inline edit or action column.
    const columns = READONLY_TAB_COLUMNS[activeTab];
    if (!columns) return null;
    return (
      <SmartTable
        {...smartTableCommonProps}
        columns={columns}
        data={readonlySliced}
        total={tabDataFiltered.length}
        isLoading={tabLoading}
        rowIdField="id"
      />
    );
  };

  const renderYearlyPerformance = () => {
    const isYearValuationIncomplete = (item: YearlyPerformance) =>
      isValuationIncomplete(item) ||
      item.startValue == null ||
      item.endValue == null ||
      item.unrealizedPnL == null;
    const estimatedBoundaryDates = (item: YearlyPerformance) => {
      const dates = [item.startEstimateCoverage, item.endEstimateCoverage]
        .flatMap((coverage) => [...(coverage?.stale ?? []), ...(coverage?.manual ?? [])])
        .map((entry) => entry.priceDate);
      return [...new Set(dates)].sort();
    };

    const yearlyPerformanceColumns: ColumnDef<YearlyPerformance>[] = [
      { accessorKey: "year", header: "Year" },
      {
        id: "valuationStatus",
        header: "Valuation",
        cell: ({ row }) => {
          const item = row.original;
          if (!isYearValuationIncomplete(item) && !item.isEstimated) return "Complete";
          if (!isYearValuationIncomplete(item) && item.isEstimated) {
            const dates = estimatedBoundaryDates(item);
            return (
              <span className="text-amber-700" title={dates.length ? `Price dates used: ${dates.join(", ")}` : undefined}>
                Estimated{dates.length ? ` · ${dates.join(", ")}` : " · old or manual prices"}
              </span>
            );
          }
          const reasons = (item.missingValuations ?? []).map(
            (missing) =>
              `${missing.symbol || missing.name || missing.symbolId || "Unknown instrument"}: ${missingValuationReasonLabel(missing.reason)}`,
          );
          return (
            <span className="text-amber-700">
              {reasons.length > 0 ? reasons.join("; ") : "Incomplete"}
            </span>
          );
        },
      },
      {
        accessorKey: "startValue",
        header: "Start Value",
        cell: ({ row }) => formatUSD(row.original.startValue),
        meta: { align: "right" },
      },
      {
        accessorKey: "endValue",
        header: "End Value",
        cell: ({ row }) => formatUSD(row.original.endValue),
        meta: { align: "right" },
      },
      {
        accessorKey: "realizedPnL",
        header: "Realized P&L",
        cell: ({ row }) => formatUSD(row.original.realizedPnL),
        meta: { align: "right" },
      },
      {
        accessorKey: "dividends",
        header: "Dividends",
        cell: ({ row }) => formatUSD(row.original.dividends),
        meta: { align: "right" },
      },
      {
        accessorKey: "unrealizedPnL",
        header: "Unreal. P&L",
        cell: ({ row }) => formatUSD(row.original.unrealizedPnL),
        meta: { align: "right" },
      },
      {
        accessorKey: "commissions",
        header: "Commissions",
        cell: ({ row }) => formatUSD(row.original.commissions),
        meta: { align: "right" },
      },
      {
        accessorKey: "taxes",
        header: "Taxes",
        cell: ({ row }) => formatUSD(row.original.taxes),
        meta: { align: "right" },
      },
      {
        accessorKey: "totalReturn",
        header: "Total Return",
        cell: ({ row }) =>
          formatUSD(
            isYearValuationIncomplete(row.original)
              ? undefined
              : row.original.totalReturn,
          ),
        meta: { align: "right" },
      },
      {
        accessorKey: "totalReturnNet",
        header: "Total Return Net",
        cell: ({ row }) =>
          formatUSD(
            isYearValuationIncomplete(row.original)
              ? undefined
              : row.original.totalReturnNet,
          ),
        meta: { align: "right" },
      },
      {
        accessorKey: "xirrRate",
        header: "XIRR",
        cell: ({ row }) =>
          formatPercent(
            isYearValuationIncomplete(row.original)
              ? undefined
              : row.original.xirrRate,
          ),
        meta: { align: "right" },
      },
    ];
    if (yearlyPerformanceAllQuery.isLoading) return <Loading />;
    if (yearlyPerformanceAllQuery.error) {
      return <ErrorState message={yearlyPerformanceAllQuery.error.message} />;
    }
    const items = yearlyPerformanceAllQuery.data?.items ?? [];
    const sorted = [...items].sort((a, b) => (a.year ?? 0) - (b.year ?? 0));
    const chartData = sorted.map((y) => ({
      year: String(y.year),
      totalReturn: isYearValuationIncomplete(y) ? null : y.totalReturn ?? null,
      totalReturnNet: isYearValuationIncomplete(y)
        ? null
        : y.totalReturnNet ?? null,
    }));

    return (
      <div className="space-y-4">
        {sorted.some(isYearValuationIncomplete) && (
          <div
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
          >
            Year-end valuation is incomplete for one or more years. Those years
            are omitted from return calculations and charts where data is
            unavailable.
          </div>
        )}
        {sorted.some((item) => item.isEstimated && !isYearValuationIncomplete(item)) && (
          <div role="status" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            Some annual results use old or manually entered prices at the start or end of the year.
          </div>
        )}
        {sorted.some((item) => item.syncStatuses?.some((status) => isPortfolioPriceSyncActive(status.status))) && (
          <div role="status" className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
            FMP is checking prices for one or more year boundaries in the background. Current annual results remain visible.
          </div>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Total Return by Year</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                No yearly data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 11 }}
                    className="text-muted-foreground"
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    className="text-muted-foreground"
                    tickFormatter={(v: number) => `$${v.toFixed(0)}`}
                  />
                  <Tooltip
                    formatter={(v) =>
                      formatUSD(
                        typeof v === "number" && Number.isFinite(v)
                          ? v
                          : undefined,
                      )
                    }
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="totalReturn"
                    name="Total Return"
                    fill="#3b82f6"
                  />
                  <Bar
                    dataKey="totalReturnNet"
                    name="Total Return Net"
                    fill="#10b981"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Yearly Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {sorted.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                No yearly data
              </div>
            ) : (
              <SmartTable
                columns={yearlyPerformanceColumns}
                data={sorted}
                total={sorted.length}
                page={1}
                pageSize={Math.max(sorted.length, 1)}
                isLoading={false}
                error={null}
                filters={[]}
                sort={[]}
                onPageChange={() => {}}
                onPageSizeChange={() => {}}
                onFiltersChange={() => {}}
                onSortChange={() => {}}
                rowIdField="year"
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Active account currency (for modal default currency pre-fill).
  const activeAccount = accounts.find((a) => a.id === activeAccountId);
  const activeAccountCurrency = activeAccount?.currency;

  // Pre-fill data for create-mode modals when a specific account is active.
  const transactionInitialData = useMemo(
    () =>
      activeAccountId === PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL
        ? undefined
        : ({
            accountId: activeAccountId,
            ...(activeAccountCurrency !== undefined
              ? { currency: activeAccountCurrency }
              : {}),
          } satisfies Partial<Transaction>),
    [activeAccountId, activeAccountCurrency],
  );
  const cashflowInitialData = useMemo(
    () =>
      activeAccountId === PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL
        ? undefined
        : ({
            accountId: activeAccountId,
            ...(activeAccountCurrency !== undefined
              ? { currency: activeAccountCurrency }
              : {}),
          } satisfies Partial<CashFlow>),
    [activeAccountId, activeAccountCurrency],
  );
  const incomeEventInitialData = useMemo(
    () =>
      activeAccountId === PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL
        ? undefined
        : ({
            accountId: activeAccountId,
            ...(activeAccountCurrency !== undefined
              ? { currency: activeAccountCurrency }
              : {}),
          } satisfies Partial<IncomeEvent>),
    [activeAccountId, activeAccountCurrency],
  );

  const actions = (
    <div className="flex items-center gap-3">
      <PortfolioSwitcher
        portfolioId={portfolioId}
        onPortfolioChange={setPortfolioId}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => setCreatePortfolioOpen(true)}
      >
        + Portfolio
      </Button>
      <CurrencySwitcher
        portfolioId={portfolioId}
        currentCurrency={displayCurrency}
      />
      <DateSelector value={date} onChange={setDate} />
      <Button variant="outline" size="sm" onClick={() => setSnapshotHistoryOpen(true)}>
        Valuation history
      </Button>
      <Select
        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
        value={activeAccountId}
        onChange={handleActiveAccountChange}
        options={[
          { value: PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL, label: "All accounts" },
          ...accounts.map((a: Account) => ({ value: a.id, label: accountLabel(a, date) })),
        ]}
        ariaLabel="Active account"
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.reload()}
      >
        Refresh
      </Button>
    </div>
  );

  const valuationIncomplete =
    isValuationIncomplete(summary) ||
    (summary !== null &&
      (summary.totalValueUSD == null || summary.totalUnrealizedPnLUSD == null));

  return (
    <DataPage title="Portfolio" actions={actions}>
      <div className="space-y-4">
        <PortfolioFreshness
          key={portfolioId}
          portfolioId={portfolioId}
          date={date}
          summary={summary}
          accounts={accountsQuery.data?.items}
          summaryFetching={summaryQuery.isFetching}
          summaryFailed={summaryQuery.isError}
          onOpenIssues={() => {
            handleTabChange(PORTFOLIO_TAB_OBJ.ACCOUNTS);
            window.requestAnimationFrame(() => {
              document.getElementById("portfolio-broker-sync-issues")?.scrollIntoView?.({ block: "start" });
            });
          }}
        />
        <ComparePeriodControls
          from={compareFrom}
          to={compareTo}
          isComparing={hasCompared && compareQuery.isFetching}
          onFromChange={(value) => {
            setCompareFrom(value);
            setHasCompared(false);
          }}
          onToChange={(value) => {
            setCompareTo(value);
            setHasCompared(false);
          }}
          onCompare={() => setHasCompared(true)}
        />
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {summary && summaryQuery.isFetching && (
              <p role="status" className="mb-3 text-sm text-muted-foreground">
                Updating portfolio summary…
              </p>
            )}
            {summary && summaryQuery.error && (
              <p role="alert" className="mb-3 text-sm text-amber-800">
                Could not refresh the summary. Showing the last available figures.
              </p>
            )}
            {summary?.syncStatus?.status === PORTFOLIO_PRICE_SYNC_STATUS_OBJ.RUNNING && (
              <p role="status" className="mb-3 text-sm text-muted-foreground">
                Checking FMP in the background. The available portfolio estimate is shown while it runs.
              </p>
            )}
            {summary?.syncStatus && ["partial", "failed"].includes(summary.syncStatus.status) && (
              <p role="status" className="mb-3 text-sm text-amber-800">
                FMP could not improve every position. The current estimate remains available.
              </p>
            )}
            {summary?.isEstimated === true && !valuationIncomplete && (
              <p role="status" className="mb-3 text-sm text-amber-800">
                Portfolio totals include old or manually entered prices. Check the price date shown for each position.
              </p>
            )}
            {loading && !summary ? (
              <Loading />
            ) : error && !summary ? (
              <ErrorState message={error} />
            ) : summary ? (
              <>
                {renderValuationNotice(
                  summary?.valuationStatus,
                  summary?.missingValuations,
                  summary?.partialValueCoverage,
                  summary?.partialUnrealizedPnLCoverage,
                  valuationIncomplete,
                )}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <SummaryMetricWithBlocked
                    label="Total Value"
                    value={formatCurrency(
                      valuationIncomplete
                        ? undefined
                        : summary?.displayValue,
                      displayCurrency,
                    )}
                    blockedMetric={summary?.blockedMetrics?.marketValue}
                    blockingState={summary?.blockedMetrics?.blockingState}
                    displayCurrency={displayCurrency}
                    valuationIncomplete={valuationIncomplete}
                    valuationDependent
                    percentUnavailableMessage="The share is hidden when the total value is zero or valuation data is incomplete."
                  />
                  <SummaryMetricWithBlocked
                    label="Invested"
                    value={formatCurrency(
                      summary?.displayInvested,
                      displayCurrency,
                    )}
                    blockedMetric={summary?.blockedMetrics?.costBasis}
                    blockingState={summary?.blockedMetrics?.blockingState}
                    displayCurrency={displayCurrency}
                    valuationIncomplete={valuationIncomplete}
                    valuationDependent={false}
                    percentUnavailableMessage="The share is hidden when the total invested amount is zero or data is incomplete."
                  />
                  <SummaryMetricWithBlocked
                    label="Unreal. P&L"
                    value={formatCurrency(
                      valuationIncomplete
                        ? undefined
                        : summary?.displayUnrealizedPnL,
                      displayCurrency,
                    )}
                    blockedMetric={summary?.blockedMetrics?.unrealizedPnL}
                    blockingState={summary?.blockedMetrics?.blockingState}
                    displayCurrency={displayCurrency}
                    valuationIncomplete={valuationIncomplete}
                    valuationDependent
                    percentUnavailableMessage="The share is hidden when either result is zero, their signs differ, or valuation data is incomplete."
                  />
                  {valuationIncomplete && (
                    <>
                      <Metric
                        label="Partial Value"
                        value={formatCurrency(summary?.displayPartialValue, displayCurrency)}
                      />
                      <Metric
                        label="Partial Unreal. P&L"
                        value={formatCurrency(summary?.displayPartialUnrealizedPnL, displayCurrency)}
                      />
                    </>
                  )}
                  <Metric
                    label="Real. P&L"
                    value={formatCurrency(
                      summary?.displayRealizedPnL,
                      displayCurrency,
                    )}
                  />
                  <Metric
                    label="Dividends"
                    value={formatCurrency(
                      summary?.displayDividends,
                      displayCurrency,
                    )}
                  />
                  <CashBalancesMetric
                    value={formatCurrency(summary?.displayCash, displayCurrency)}
                    cashBalances={summary?.cashBalances}
                    displayCurrency={displayCurrency}
                    accountNames={accountNames}
                    closedAccountIds={closedAccountIds}
                  />
                  <Metric
                    label="Commissions"
                    value={formatCurrency(
                      summary?.displayCommissions,
                      displayCurrency,
                    )}
                  />
                  <Metric
                    label="Taxes"
                    value={formatCurrency(summary?.displayTaxes, displayCurrency)}
                  />
                  <Metric
                    label="Deposits"
                    value={formatCurrency(
                      summary?.displayDeposits,
                      displayCurrency,
                    )}
                  />
                  <Metric
                    label="Withdrawals"
                    value={formatCurrency(
                      summary?.displayWithdrawals,
                      displayCurrency,
                    )}
                  />
                  <Metric
                    label="Total Return"
                    value={formatCurrency(
                      valuationIncomplete ? undefined : summary?.displayReturn,
                      displayCurrency,
                    )}
                  />
                  <Metric
                    label="Total Return Net"
                    value={formatCurrency(
                      valuationIncomplete
                        ? undefined
                        : summary?.displayReturnNet,
                      displayCurrency,
                    )}
                  />
                  <Metric
                    label="XIRR"
                    value={formatPercent(
                      valuationIncomplete ? undefined : summary?.xirr,
                    )}
                  />
                  <Metric
                    label="Dividend Yield"
                    value={formatPercent(
                      valuationIncomplete
                        ? undefined
                        : summary?.totalDividendYield,
                    )}
                  />
                </div>
              </>
            ) : (
              <Loading />
            )}
          </CardContent>
        </Card>

        <ComparePeriodResults
          from={compareFrom}
          to={compareTo}
          data={hasCompared ? (compareQuery.data ?? null) : null}
          isLoading={hasCompared && compareQuery.isLoading}
          error={hasCompared ? (compareQuery.error?.message ?? null) : null}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {renderAllocations(
            "By Asset Class",
            valuationIncomplete
              ? allocations.partialAllocationByAssetClass
              : allocations.allocationByAssetClass,
            valuationIncomplete
              ? allocations.partialAllocationAmountsByAssetClass
              : allocations.allocationAmountsByAssetClass,
            valuationIncomplete,
          )}
          {renderAllocations(
            "By Currency",
            valuationIncomplete
              ? allocations.partialAllocationByCurrency
              : allocations.allocationByCurrency,
            valuationIncomplete
              ? allocations.partialAllocationAmountsByCurrency
              : allocations.allocationAmountsByCurrency,
            valuationIncomplete,
          )}
          {renderAccountAllocations(
            valuationIncomplete
              ? allocations.partialAllocationByAccount
              : allocations.allocationByAccount,
            valuationIncomplete,
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Primary buttons — always visible */}
          <Button
            size="sm"
            onClick={() => setModal(PORTFOLIO_MODAL_OBJ.TRANSACTION)}
          >
            + Add Transaction
          </Button>
          {activeTab === PORTFOLIO_TAB_OBJ.TRANSACTIONS && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setModal(PORTFOLIO_MODAL_OBJ.IMPORT_TRANSACTIONS)}
            >
              Import CSV
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => setModal(PORTFOLIO_MODAL_OBJ.CASHFLOW)}
          >
            + Add Cash Flow
          </Button>
          <Button
            size="sm"
            onClick={() => setModal(PORTFOLIO_MODAL_OBJ.INCOME_EVENT)}
          >
            + Add Income Event
          </Button>

          {/* Secondary buttons — grouped in "More" dropdown */}
          <div className="relative" ref={moreRef}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMoreOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
            >
              More <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
            {moreOpen && (
              <div
                className="absolute z-20 mt-1 min-w-[200px] rounded-md border bg-background shadow-lg"
                role="menu"
              >
                <button
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setModal(PORTFOLIO_MODAL_OBJ.SYMBOL);
                    setMoreOpen(false);
                  }}
                >
                  + Add Symbol
                </button>
                <button
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setModal(PORTFOLIO_MODAL_OBJ.ACCOUNT);
                    setMoreOpen(false);
                  }}
                >
                  + Add Account
                </button>
                <button
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setModal(PORTFOLIO_MODAL_OBJ.EXCHANGE_RATE);
                    setMoreOpen(false);
                  }}
                >
                  + Add Manual Rate
                </button>
                <button
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setModal(PORTFOLIO_MODAL_OBJ.FETCH_RATE);
                    setMoreOpen(false);
                  }}
                >
                  Fetch by Date
                </button>
                <div className="border-t" />
                <button
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                  disabled={refreshRates.isPending}
                  onClick={handleRefreshRates}
                >
                  {refreshRates.isPending ? "Refreshing..." : "↻ Refresh Rates"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1 border-b pb-0">
          {TABS.map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? "secondary" : "ghost"}
              size="sm"
              onClick={() => {
                handleTabChange(tab);
              }}
              className="rounded-b-none"
            >
              {tab}
            </Button>
          ))}
        </div>

        <div>{renderTabContent()}</div>
      </div>

      {/* Add modals (create mode) — invalidation auto-happens in mutation onSuccess */}
      <AddTransactionModal
        open={modal === PORTFOLIO_MODAL_OBJ.TRANSACTION}
        onClose={() => {
          setModal(null);
          setTransactionPrefill(undefined);
        }}
        portfolioId={portfolioId}
        initialData={transactionPrefill ?? transactionInitialData}
      />
      <ImportTransactionsModal
        open={modal === PORTFOLIO_MODAL_OBJ.IMPORT_TRANSACTIONS}
        onClose={() => setModal(null)}
        portfolioId={portfolioId}
      />
      <AddCashFlowModal
        open={modal === PORTFOLIO_MODAL_OBJ.CASHFLOW}
        onClose={() => setModal(null)}
        portfolioId={portfolioId}
        initialData={cashflowInitialData}
      />
      <AddIncomeEventModal
        open={modal === PORTFOLIO_MODAL_OBJ.INCOME_EVENT}
        onClose={() => setModal(null)}
        portfolioId={portfolioId}
        initialData={incomeEventInitialData}
      />
      <AddAccountModal
        open={modal === PORTFOLIO_MODAL_OBJ.ACCOUNT}
        onClose={() => setModal(null)}
        portfolioId={portfolioId}
      />
      <AddSymbolModal
        open={modal === PORTFOLIO_MODAL_OBJ.SYMBOL}
        onClose={() => setModal(null)}
      />
      <CloseAccountModal
        portfolioId={portfolioId}
        account={closingAccount}
        defaultDate={date}
        onClose={() => setClosingAccountId(null)}
        onClosed={(closed) => showToast("success", `Счёт «${closed.name}» закрыт ${closed.closedOn ?? ""}`.trim())}
      />
      <EditBrokerSyncAccountModal
        open={editingAccountId !== null}
        account={editingAccount}
        onClose={() => setEditingAccountId(null)}
      />

      <TransactionBlockModal
        portfolioId={portfolioId}
        transaction={blockingTransaction}
        onClose={() => setBlockingTransaction(null)}
        onSaved={(savedTransaction) => {
          if (!savedTransaction.id) return;
          setPendingBlockConfirmation({
            id: savedTransaction.id,
            blockedQuantity: savedTransaction.blockedQuantity ?? 0,
          });
        }}
      />
      <AddExchangeRateModal
        open={modal === PORTFOLIO_MODAL_OBJ.EXCHANGE_RATE}
        onClose={() => setModal(null)}
      />
      <FetchExchangeRateModal
        open={modal === PORTFOLIO_MODAL_OBJ.FETCH_RATE}
        onClose={() => setModal(null)}
      />
      <CreatePortfolioModal
        open={createPortfolioOpen}
        onClose={() => setCreatePortfolioOpen(false)}
        onCreated={(p) => setPortfolioId(p.id)}
      />
      <ManualPriceModal
        portfolioId={portfolioId}
        target={manualPriceTarget}
        onClose={() => setManualPriceTarget(null)}
      />
      <ProviderIdentityModal
        portfolioId={portfolioId}
        target={providerIdentityTarget}
        onClose={() => setProviderIdentityTarget(null)}
      />
      <CorporateEventModal
        portfolioId={portfolioId}
        target={corporateEventTarget}
        accounts={accounts.map((account) => ({ id: account.id, name: account.name }))}
        {...(activeAccountId !== PORTFOLIO_ACCOUNT_FILTER_OBJ.ALL ? { defaultAccountId: activeAccountId } : {})}
        onClose={() => setCorporateEventTarget(null)}
      />
      <SnapshotRevisionModal
        open={snapshotHistoryOpen}
        portfolioId={portfolioId}
        date={date}
        onClose={() => setSnapshotHistoryOpen(false)}
      />

      {/* Edit modals (edit mode via editId + initialData) */}
      <AddTransactionModal
        open={editTransaction !== null}
        editId={editTransaction?.id}
        initialData={editTransaction ?? undefined}
        portfolioId={portfolioId}
        onClose={() => setEditTransaction(null)}
      />
      <AddIncomeEventModal
        open={editIncomeEvent !== null}
        editId={editIncomeEvent?.id}
        initialData={editIncomeEvent ?? undefined}
        portfolioId={portfolioId}
        onClose={() => setEditIncomeEvent(null)}
      />
      <AddSymbolModal
        open={editSymbol !== null}
        editId={editSymbol?.id}
        initialData={editSymbol ?? undefined}
        onClose={() => setEditSymbol(null)}
      />

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </DataPage>
  );
};
